import { ref, watch, nextTick, computed } from 'vue'
import { defineStore } from 'pinia'
import { useDefineFiltersQuery, useShoppingListQuery } from '@/queries/search'
import { queryCache, serializeQueryKey } from '@/queries'
import { findFilterValueElementInsideDynamicFilters } from '@/helpers/FindFilterValueElementInsideDynamicFilters'
import type {
  arrayOfFilters,
  FilterItem,
  Pagination,
  staticFilters as StaticFilters,
  MultiSelectItem
} from '@/types/types'
import type {
  AppliedBand,
  AppliedFacetMode,
  AppliedQueryEntity,
  payloadFilter,
  shoppingListParams,
  shoppingListPayload,
  SortingOptions,
  SuggestedFilter
} from '@/types/searchItemsTypes'
import { deriveAutoAppliedChips } from '@/features/search/lib/autoAppliedChips'
import type { AutoAppliedChip } from '@/features/search/lib/autoAppliedChips'

export type { AutoAppliedChip } from '@/features/search/lib/autoAppliedChips'
import type { ProductCustomCardItem } from '@/types/ProductType'

const INITIAL_STATIC_FILTERS: StaticFilters = {
  original: false,
  high_rating: false
}

export type ItemsBehavior = 'default' | 'add'

export const useSearch = defineStore('search', () => {
  // Убираем ref для ошибок - будем использовать напрямую из queries через computed
  // Это уберет необходимость в watch и уменьшит количество микрозадач

  const suggestedFilters = ref<SuggestedFilter[]>([])
  const currentFilters = ref<arrayOfFilters>([])
  const staticFiltersState = ref<StaticFilters>({ ...INITIAL_STATIC_FILTERS })

  // define-filters returns the query's applied entities in the search-payload
  // shape (filter_option present); it seeds the first search's dynamic_filters.
  const responseDefineFilters = ref<payloadFilter[]>([])
  const countOfItemInRow = ref(4)
  const currentSort = ref<SortingOptions>('default')
  const pageInfo = ref<Pagination | null>(null)
  const itemsBehavior = ref<ItemsBehavior>('default')
  const searchResult = ref<ProductCustomCardItem[]>([])
  const totalQuantity = ref(0)
  const generatedImageLink = ref<string | null>(null)
  const appliedFacetModes = ref<AppliedFacetMode[]>([])
  const appliedBands = ref<AppliedBand[]>([])
  const appliedEntities = ref<AppliedQueryEntity[]>([])

  const currentSearchText = ref<string | null>(null)
  const defineFiltersQuery = useDefineFiltersQuery(currentSearchText)

  watch(
    () => defineFiltersQuery.data.value,
    (data) => {
      if (!data) return
      responseDefineFilters.value = data.defined_filters ?? []
    }
  )

  const defineFilters = async (searchText: string) => {
    currentSearchText.value = searchText
    await defineFiltersQuery.refresh(true)
    const error = defineFiltersQuery.error.value
    if (error) {
      throw error
    }
    return defineFiltersQuery.data.value
  }

  const shoppingPayload = ref<shoppingListPayload | null>(null)
  const shoppingParams = ref<shoppingListParams | null>(null)
  // Continuation token for the current search; paging reuses the backend's
  // cached candidates instead of recomputing similarity.
  const chainId = ref<string | null>(null)
  const chainScope = ref<string | null>(null)
  // A pending chain can take minutes to resolve; without this a superseded
  // search would still land and overwrite the results the user is now looking at.
  const activeSignal = ref<AbortSignal | undefined>(undefined)
  let activeController: AbortController | null = null
  const shoppingListQuery = useShoppingListQuery(
    shoppingPayload,
    shoppingParams,
    undefined,
    chainId,
    activeSignal
  )

  watch(
    () => shoppingListQuery.data.value,
    (data) => {
      if (!data) return
      suggestedFilters.value = data.suggested_filters ?? []
      pageInfo.value = data.page_info ?? null
      searchResult.value = data.search_results ?? []
      // FACET-1: dynamic_filters is null on pages 2+ — "not recomputed, keep
      // the page-1 payload" (FE_CONTRACT_CHANGES.md). Only [] means empty.
      currentFilters.value = data.dynamic_filters ?? currentFilters.value
      totalQuantity.value = data.total_quantity ?? 0
      generatedImageLink.value = data.generated_image_url ?? null
      staticFiltersState.value = data.static_filters ?? { ...INITIAL_STATIC_FILTERS }
      appliedFacetModes.value = data.applied_facet_modes ?? []
      appliedBands.value = data.applied_bands ?? []
      appliedEntities.value = data.applied_entities ?? []
      // The backend may hand back a different chain than we submitted (expired
      // cache restarts one) — always follow the chain it just named.
      if (data.chain_id) {
        chainId.value = data.chain_id
      }
    }
  )

  // Упрощенная версия getSearchList - используем промис напрямую из cache без until и while
  const getSearchList = async (payload: shoppingListPayload, params: shoppingListParams) => {
    // Только смена страницы продолжает цепочку; новый запрос/фильтры/сортировка
    // начинают новую, иначе бекенд отдал бы срез чужого кэша кандидатов.
    const scope = JSON.stringify(payload)
    if (scope !== chainScope.value) {
      chainScope.value = scope
      chainId.value = null
      // Stop waiting on the superseded search's chain.
      activeController?.abort()
      activeController = null
    }

    if (!activeController) {
      activeController = new AbortController()
      activeSignal.value = activeController.signal
    }

    // createQuery keeps the last error until a request succeeds; a retry must
    // not immediately rethrow the previous attempt's failure.
    shoppingListQuery.error.value = null

    // Обновляем payload и params - это автоматически вызовет запрос через watch в createQuery
    shoppingPayload.value = payload
    shoppingParams.value = params

    // Даем Vue время обновить реактивность и запустить watch в createQuery
    await nextTick()

    // Получаем ключ для текущего запроса и проверяем, есть ли промис в кэше
    // Используем промис напрямую, если он есть - это избегает создания watch инстансов
    const key: readonly unknown[] = ["search", "shopping-list", payload, params]
    const serializedKey = serializeQueryKey(key)

    // Проверяем cache после nextTick, чтобы дать время watch создать промис
    await nextTick()
    const cached = queryCache.getBySerializedKey(serializedKey)

    // Если есть промис в кэше, дожидаемся его завершения напрямую
    // Это самый эффективный способ - без создания watch инстансов
    if (cached?.promise) {
      try {
        await cached.promise
      } catch {
        // Ошибка уже обработана в createQuery
      }
    }
    // Если промиса нет, значит запрос уже завершился или еще не начался
    // В таком случае просто проверяем результат

    const error = shoppingListQuery.error.value
    if (error) {
      throw error
    }
    return shoppingListQuery.data.value
  }

  function updateGeneralFilterFilterValue(
    generalFilterLabel: string,
    name: string,
    value: boolean
  ) {
    const item = findFilterValueElementInsideDynamicFilters(
      currentFilters.value,
      generalFilterLabel,
      name
    )
    if (item) {
      item.is_selected = value
    }
    return item
  }

  function applyGeneralFilterValuesWithValuesArray(
    generalFilterLabel: string,
    newValues: MultiSelectItem[] = []
  ) {
    const generalFilterToUpdate = currentFilters.value?.find(
      (generalFilter) => generalFilter.options.label === generalFilterLabel
    )

    if (generalFilterToUpdate) {
      generalFilterToUpdate.options.values = newValues
    }
  }

  function updateStaticFilters(key: keyof StaticFilters, value: boolean) {
    staticFiltersState.value = {
      ...staticFiltersState.value,
      [key]: value
    }
  }

  const changePriceFromUserMenu = async (min: number, max: number, _indexOfCategory: number) => {
    if (!currentFilters.value) {
      return
    }

    const priceSelectGeneralFilter = currentFilters.value.find(
      (generalFilter) => generalFilter.type === 'graph_select'
    )

    if (priceSelectGeneralFilter) {
      priceSelectGeneralFilter.options.min_filter_value = min
      priceSelectGeneralFilter.options.max_filter_value = max
    }
  }

  const clearAllFilters = async () => {
    currentFilters.value?.forEach((currentFiltersItem) => {
      currentFiltersItem.options.values?.forEach((item) => {
        item.is_selected = false
      })
    })
  }

  const clearFiltersToSelectedFilterType = async (item: FilterItem) => {
    const selectedItem = currentFilters.value?.find(
      (currentFiltersItem) => currentFiltersItem.options.label === item.options.label
    )
    if (!selectedItem) return
    selectedItem.options.values?.forEach((filterItem) => {
      filterItem.is_selected = false
    })
  }

  const setItemsBehavior = (behavior: ItemsBehavior) => {
    itemsBehavior.value = behavior
  }

  const setSortingOptions = (sort: SortingOptions) => {
    currentSort.value = sort
  }

  // Используем computed вместо watch для уменьшения количества микрозадач
  // Это избавляет от необходимости в watch с immediate: true, которые создают лишние микрозадачи
  // Server-applied constraints reported by the response (no user chip covers
  // them). Entity chips win once the backend ships the hard pyramid; the
  // facet/band derivation is the fallback for older responses. Facet entries
  // duplicating a user-selected filter are skipped — the user chip already
  // represents that constraint and stays the dismiss surface.
  const autoAppliedChips = computed<AutoAppliedChip[]>(() => {
    const userSelectedOptions = new Set(
      (currentFilters.value ?? [])
        .filter((filter) => filter.options.values?.some((value) => value.is_selected))
        .map((filter) => filter.options.label)
    )

    return deriveAutoAppliedChips(
      appliedEntities.value,
      appliedFacetModes.value,
      appliedBands.value,
      userSelectedOptions
    )
  })

  const isDefineFiltersLoading = computed(() => defineFiltersQuery.isLoading.value)
  const isGetSearchListLoading = computed(() => shoppingListQuery.isLoading.value)
  const defineFiltersError = computed(() => defineFiltersQuery.error.value?.message ?? null)
  const getSearchListError = computed(() => shoppingListQuery.error.value?.message ?? null)

  return {
    getSearchList,
    defineFilters,
    currentFilters,
    staticFilters: staticFiltersState,
    responseDefineFilters,
    countOfItemInRow,
    currentSort,
    suggestedFilters,
    updateGeneralFilterFilterValue,
    updateStaticFilters,
    applyGeneralFilterValuesWithValuesArray,
    changePriceFromUserMenu,
    clearAllFilters,
    clearFiltersToSelectedFilterType,
    pageInfo,
    itemsBehavior,
    setItemsBehavior,
    setSortingOptions,
    searchResult,
    totalQuantity,
    generatedImageLink,
    chainId,
    appliedFacetModes,
    appliedBands,
    appliedEntities,
    autoAppliedChips,
    isDefineFiltersLoading,
    isGetSearchListLoading,
    defineFiltersError,
    getSearchListError
  }
})

